import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { InputField } from "@/components/form/InputField";
import { SelectField } from "@/components/form/SelectField";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  content: z.string().min(1, "Content is required"),
  child_id: z.string().optional(),
});

export const JournalEntryForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      content: "",
      child_id: undefined,
    },
  });

  const { data: children = [] } = useQuery({
    queryKey: ["childProfiles"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No authenticated session");

      const { data, error } = await supabase
        .from("child_profiles")
        .select("*")
        .eq('parent_id', session.user.id);
      
      if (error) throw error;
      return data || [];
    },
  });

  const childOptions = children.map(child => ({
    value: child.id,
    label: child.name,
  }));

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No authenticated session");

      const entry = {
        title: values.title,
        content: values.content,
        child_id: values.child_id,
        user_id: session.user.id,
        category: "other", // Set a default category since we removed the selection
      };

      const { error } = await supabase
        .from("journal_entries")
        .insert(entry);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Journal entry saved successfully",
        duration: 500,
      });

      queryClient.invalidateQueries({ queryKey: ["journalEntries"] });
      form.reset();
      onSuccess();
    } catch (error) {
      console.error("Error saving journal entry:", error);
      toast({
        title: "Error",
        description: "Failed to save journal entry",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <InputField
          form={form}
          name="title"
          label="Title"
          placeholder="Enter entry title"
        />

        <SelectField
          form={form}
          name="child_id"
          label="Child (Optional)"
          placeholder="Select a child"
          options={childOptions}
        />

        <div className="space-y-2">
          <label className="text-sm font-medium">Content</label>
          <Textarea
            {...form.register("content")}
            placeholder="Write your journal entry here..."
            className="min-h-[200px]"
          />
          {form.formState.errors.content && (
            <p className="text-sm text-red-500">
              {form.formState.errors.content.message}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full">
          Save Entry
        </Button>
      </form>
    </Form>
  );
};